/**
 * KAN-770: files served by the authenticated API (MEDIA is not served on
 * production). A bare <img src> or <a href> sends no token, so such a file is
 * fetched through the api client (Bearer header) as a blob.
 */
import { apiClient } from './client';

/** True for a path to the authenticated API; any other URL (S3 presigned) opens as is. */
export function isApiFileUrl(url: string): boolean {
  return url.startsWith('/api/');
}

/** File from a closed endpoint as a blob. */
export async function fetchApiFile(url: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(url, { responseType: 'blob' });
  return data;
}

/** Object URLs of one-off downloads live this long (the browser needs them after click). */
export const OBJECT_URL_TTL_MS = 60_000;

/** Revoke an object URL after OBJECT_URL_TTL_MS; the opened tab keeps what it loaded. */
export function revokeObjectUrlLater(objectUrl: string): void {
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_TTL_MS);
}

/**
 * KAN-785: revoke an object URL when the tab that shows it is closed (a video is
 * fetched lazily by range requests, so a timer would break seeking). `pagehide`
 * on the tab also fires when about:blank navigates to the blob URL, hence the
 * `closed` check on the next tick; while the tab lives the listener is re-armed.
 * If the tab went to another origin, `addEventListener` throws (SecurityError):
 * the blob is not shown there any more, so the URL is revoked at once. Without a
 * tab handle (popup blocked) nothing is done: the browser drops the blob URL
 * together with this document.
 */
export function revokeObjectUrlOnClose(tab: Window | null, objectUrl: string): void {
  if (!tab) return;
  const revoke = () => URL.revokeObjectURL(objectUrl);
  const arm = (): void => {
    try {
      tab.addEventListener('pagehide', onHide, { once: true });
    } catch {
      revoke();
    }
  };
  const onHide = (): void => {
    window.setTimeout(() => {
      if (tab.closed) revoke();
      else arm();
    }, 0);
  };
  arm();
}

/** Schemes an address outside the API may have to be opened in a tab. */
const EXTERNAL_PROTOCOLS = ['https:', 'http:'];

/**
 * KAN-809: open an address outside the API in a new tab. The address comes
 * from message data, so only http(s) is opened; any other scheme
 * (javascript:, data:, a foreign blob:) or an unparsable address throws, the
 * same way a failed load does.
 */
function openExternalFile(url: string): void {
  let protocol = '';
  try {
    protocol = new URL(url, window.location.href).protocol;
  } catch {
    protocol = '';
  }
  if (!EXTERNAL_PROTOCOLS.includes(protocol)) {
    throw new Error('Unsupported file url scheme');
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Save a file: an API path is fetched with the token and saved under
 * `filename`; any other http(s) URL opens in a new tab.
 */
export async function downloadApiFile(url: string, filename: string): Promise<void> {
  if (!isApiFileUrl(url)) {
    openExternalFile(url);
    return;
  }
  const objectUrl = URL.createObjectURL(await fetchApiFile(url));
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename || 'file';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  revokeObjectUrlLater(objectUrl);
}

/**
 * Open a file (picture) in a new tab: an API path is fetched with the token
 * into an object URL. The tab is opened before the fetch, otherwise the popup
 * blocker stops it; on failure the tab is closed and the error rethrown.
 */
export async function openApiFile(url: string): Promise<void> {
  if (!isApiFileUrl(url)) {
    openExternalFile(url);
    return;
  }
  const tab = window.open('', '_blank');
  try {
    const objectUrl = URL.createObjectURL(await fetchApiFile(url));
    if (tab) tab.location.href = objectUrl;
    revokeObjectUrlLater(objectUrl);
  } catch (err) {
    tab?.close();
    throw err;
  }
}
